import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ExitToApp } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { logout } from '../../store/actions/auth';
import styles from './LogoutButton.module.scss';

/**
 * Pill-shaped logout button that matches the LanguageSwitcher design
 * language. Used in both the sidebar footer and the top navbar.
 *
 * Props:
 *   size      — "md" (default) or "sm" (compact, for navbar/sidebar footer)
 *   showText  — when true, renders the localized "Log out" label next to
 *               the icon. Defaults to false (icon-only).
 *   onBeforeLogout — optional callback. When provided the button delegates
 *                    the logout flow entirely to the parent (e.g. the
 *                    sidebar fades out and dispatches logout itself).
 *                    When omitted the button dispatches logout directly.
 */
const LogoutButton = ({ size = 'md', showText = false, onBeforeLogout, className = '' }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleClick = () => {
        if (onBeforeLogout) {
            onBeforeLogout();
        } else {
            dispatch(logout(navigate));
        }
    };

    const label = t('logout');

    return (
        <Tooltip title={t('logout_title')} placement="top">
            <button
                type="button"
                data-testid="logout-button"
                className={`${styles.logoutBtn} ${styles[`size_${size}`]} ${
                    showText ? styles.withText : ''
                } ${className}`}
                onClick={handleClick}
                aria-label={t('logout_title')}
            >
                <ExitToApp className={styles.icon} />
                {showText && <span className={styles.text}>{label}</span>}
            </button>
        </Tooltip>
    );
};

export default LogoutButton;
